/**
 * Offline mutation queue (Pillar B.1 — offline is NORMAL for the crew app).
 *
 * A small IndexedDB-backed FIFO of mutations that failed on a dead network
 * (task transitions, notes). Each entry keeps the Idempotency-Key generated
 * at tap time, so a replay that raced its original send can't apply twice —
 * the server scopes keys per caller + method + path (ADR-0014).
 *
 * Replay semantics on flush:
 * - network error → still offline: stop, keep everything, preserve order
 * - HTTP error    → the server SAW it and said no (validation / authz /
 *   state-machine): drop it and report — retrying forever can't succeed,
 *   and field truth has moved on.
 */
import { apiCall, ApiError } from './api';

export interface QueuedMutation {
  id: string;
  path: string;
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body: unknown;
  idempotencyKey: string;
  queuedAt: string;
  /** Human description for sync toasts, e.g. "Complete · Install desks". */
  label: string;
}

export interface FlushResult {
  synced: number;
  /** Entries the server rejected (dropped, with reasons for the user). */
  rejected: Array<{ label: string; message: string }>;
  remaining: number;
}

const DB_NAME = 'vari-crew-offline';
const DB_VERSION = 1;
const STORE = 'mutations';

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** fetch() rejects with TypeError on network failure; HTTP errors are ApiError. */
export function isNetworkError(err: unknown): boolean {
  return !(err instanceof ApiError) && err instanceof TypeError;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
    });
  } finally {
    db.close();
  }
}

// Listeners let the shell render a live "N pending" badge without polling.
const listeners = new Set<() => void>();
function notify(): void {
  listeners.forEach((l) => l());
}
export function subscribeQueue(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function enqueueMutation(
  entry: Omit<QueuedMutation, 'id' | 'queuedAt'>,
): Promise<void> {
  const row: QueuedMutation = {
    ...entry,
    id: crypto.randomUUID(),
    queuedAt: new Date().toISOString(),
  };
  await withStore('readwrite', (s) => s.add(row));
  notify();
}

export async function pendingMutations(): Promise<QueuedMutation[]> {
  const rows = await withStore<QueuedMutation[]>('readonly', (s) =>
    s.getAll(),
  );
  return rows.sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
}

export async function pendingCount(): Promise<number> {
  return withStore<number>('readonly', (s) => s.count());
}

async function removeMutation(id: string): Promise<void> {
  await withStore('readwrite', (s) => s.delete(id));
}

let flushing = false;

/** Replay the queue FIFO. Safe to call repeatedly; concurrent calls no-op. */
export async function flushQueue(): Promise<FlushResult> {
  if (flushing) {
    return { synced: 0, rejected: [], remaining: await pendingCount() };
  }
  flushing = true;
  try {
    const items = await pendingMutations();
    let synced = 0;
    const rejected: FlushResult['rejected'] = [];
    for (const m of items) {
      try {
        await apiCall(m.path, {
          method: m.method,
          body: m.body,
          headers: { 'Idempotency-Key': m.idempotencyKey },
        });
        await removeMutation(m.id);
        synced += 1;
      } catch (err) {
        if (isNetworkError(err)) break; // still offline — keep order, try later
        await removeMutation(m.id);
        rejected.push({ label: m.label, message: (err as Error).message });
      }
    }
    if (synced > 0 || rejected.length > 0) notify();
    return { synced, rejected, remaining: (await pendingCount()) };
  } finally {
    flushing = false;
  }
}
